import {
  FinancialApplicationFormSectionSummary,
  FinancialApplicationFormSectionSummaryDataRow,
} from "../FinancialApplicationFormSectionSummary/FinancialApplicationFormSectionSummary";
import { useFieldSchemaToGetDataForJointOwnerSummary } from "../FinancialApplicationFormSectionSummary/useFieldSchemaToGetDataForSummary";
import { FieldInput, getHiddenGovernmentIdFields } from "@/utils/getFieldSchemaData";
import { COMPLEX_FIELD_KEYS, FIELD_KEYS } from "@/constants/fieldKeys";
import { useJointAccountOwnersSummary } from "@/hooks/useJointAccountOwnersSummary";
import { useMemo } from "react";
import { STEP_PATHS } from "@/config/stepPaths";

export interface JointAccountOwnersSummaryProps {
  financialApplicationId: string;
}

export const JointAccountOwnersSummaryV2: React.FC<JointAccountOwnersSummaryProps> = ({
  financialApplicationId,
}) => {
  const { jointAccountOwners, isLoading } = useJointAccountOwnersSummary(financialApplicationId);

  const summaryData = useMemo(() => {
    const data: FinancialApplicationFormSectionSummaryDataRow[] = [];

    // Process each joint account owner
    jointAccountOwners.forEach((owner) => {
      // Use the reusable utility function to determine hidden government ID fields
      const hiddenGovernmentIdFields = getHiddenGovernmentIdFields(owner.governmentIdInformation);
      
      const fieldsToInclude: FieldInput[] = [
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.RELATIONSHIP_TO_PRIMARY_OWNER,
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.HOME_ADDRESS_IS_SAME_AS_ACCOUNT_OPENER,
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.PERSON_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.GOVERNMENT_ID_INFORMATION,
          complexFieldValue: {
            hiddenFields: hiddenGovernmentIdFields,
          },
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.EMPLOYMENT_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.DIGITAL_BANKING_INFORMATION,
          complexFieldValue: {
            hiddenFields: [
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_USERNAME,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_ROLE,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_EMAIL_ADDRESS,
              COMPLEX_FIELD_KEYS.DIGITAL_BANKING_INFORMATION.DIGITAL_BANKING_PHONE_NUMBER,
            ],
          },
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.SECURITY_QUESTIONS_INFORMATION,
        },
        {
          fieldProps: FIELD_KEYS.JOINT_ACCOUNT_OWNER.DEBIT_CARD_INFORMATION,
          complexFieldValue: {
            hiddenFields: [
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_NAME_DISPLAYED,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.BUSINESS_NAME_DISPLAYED_ON_CARD,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.USE_HOME_ADDRESS_FOR_DEBIT_CARD_MAILING_ADDRESS,
              COMPLEX_FIELD_KEYS.DEBIT_CARD_INFORMATION.DEBIT_CARD_MAILING_ADDRESS,
            ],
          },
        },
      ];
      
      const ownerSummaryData = useFieldSchemaToGetDataForJointOwnerSummary(owner, fieldsToInclude);
      data.push(...ownerSummaryData);
    });

    return data;
  }, [jointAccountOwners]);

  if (isLoading) {
    return null; // Or a loading spinner if preferred
  }

  return (
    <FinancialApplicationFormSectionSummary
      title="Joint Account Owners"
      data={summaryData}
      navigateTo={`/step/${STEP_PATHS.JointOwners}`}
    />
  );
};
